import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { 
  Upload, 
  Trash2, 
  Receipt,
  Camera,
  Edit3,
  ArrowRight,
  Loader2,
  Image as ImageIcon,
  CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ReceiptsTab = ({ businessId, token, authHeaders, categories }) => {
  const { toast } = useToast();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [editForm, setEditForm] = useState({
    vendor: '',
    amount: '',
    date: '',
    category_id: '',
    description: ''
  });

  const fetchReceipts = async () => {
    if (!businessId) return;
    try {
      const response = await axios.get(`${API}/api/mybookie/receipts/${businessId}`, authHeaders);
      setReceipts(response.data.receipts);
    } catch (error) {
      console.error("ReceiptsTab error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts();
  }, [businessId]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: "Error", description: "Please upload an image file", variant: "destructive" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Error", description: "File too large (max 5MB)", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      // Upload the file
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(`${API}/api/mybookie/upload/receipt`, formData, {
        headers: {
          ...authHeaders.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Create receipt record
      await axios.post(`${API}/api/mybookie/receipts`, {
        business_id: businessId,
        image_url: uploadRes.data.url
      }, authHeaders);

      toast({ title: "Success", description: "Receipt uploaded! Add details to convert to expense." });
      fetchReceipts();
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload receipt", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEditReceipt = (receipt) => {
    setEditForm({
      vendor: receipt.vendor || '',
      amount: receipt.amount || '',
      date: receipt.date || '',
      category_id: receipt.category_id || '',
      description: receipt.description || ''
    });
    setShowEdit(receipt);
  };

  const handleSaveReceipt = async () => {
    try {
      const params = new URLSearchParams();
      if (editForm.vendor) params.append('vendor', editForm.vendor);
      if (editForm.amount) params.append('amount', editForm.amount);
      if (editForm.date) params.append('date', editForm.date);
      if (editForm.category_id) params.append('category_id', editForm.category_id);
      if (editForm.description) params.append('description', editForm.description);

      await axios.put(`${API}/api/mybookie/receipts/${showEdit.id}?${params.toString()}`, null, authHeaders);
      
      toast({ title: "Success", description: "Receipt updated" });
      setShowEdit(null);
      fetchReceipts();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update receipt", variant: "destructive" });
    }
  };

  const handleConvertToExpense = async (receiptId) => {
    try {
      await axios.post(`${API}/api/mybookie/receipts/${receiptId}/convert-to-expense`, null, authHeaders);
      toast({ title: "Success", description: "Expense created from receipt!" });
      fetchReceipts();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to convert receipt";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/receipts/${receiptId}`, authHeaders);
      toast({ title: "Success", description: "Receipt deleted" });
      fetchReceipts();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete receipt", variant: "destructive" });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const expenseCategories = categories?.expense_categories || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A2B1D]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <Card className="border-[#E6E8E6] border-dashed border-2 bg-[#F6F5F2]/50">
        <CardContent className="p-8">
          <div className="text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="receipt-upload"
            />
            <label htmlFor="receipt-upload" className="cursor-pointer">
              <div className="mx-auto w-16 h-16 bg-[#E2ECE7] rounded-sm flex items-center justify-center mb-4">
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-[#0A2B1D] animate-spin" />
                ) : (
                  <Upload className="w-8 h-8 text-[#0A2B1D]" />
                )}
              </div>
              <p className="text-lg font-medium text-[#111614] mb-1">
                {uploading ? 'Uploading...' : 'Upload Receipt'}
              </p>
              <p className="text-sm text-[#5C6661]">
                Click to browse or drag and drop
              </p>
              <p className="text-xs text-[#8A948F] mt-2">
                PNG, JPG up to 5MB
              </p>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Receipts Grid */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-[#111614]">Your Receipts</h3>
        <span className="text-sm text-[#5C6661]">{receipts.length} receipts</span>
      </div>

      {receipts.length === 0 ? (
        <Card className="border-[#E6E8E6]">
          <CardContent className="p-8 text-center text-[#5C6661]">
            <Receipt className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No receipts yet. Upload your first receipt!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {receipts.map((receipt) => (
            <Card key={receipt.id} className="border-[#E6E8E6] overflow-hidden">
              <div 
                className="h-40 bg-[#F6F5F2] flex items-center justify-center cursor-pointer relative"
                onClick={() => setShowPreview(receipt)}
              >
                {receipt.image_url ? (
                  <img 
                    src={`${API}${receipt.image_url}`} 
                    alt="Receipt" 
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-[#8A948F]" />
                )}
                {receipt.linked_expense_id && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-sm text-xs flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Linked
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <div className="mb-3">
                  <p className="font-medium text-[#111614]">
                    {receipt.vendor || 'Unknown Vendor'}
                  </p>
                  {receipt.amount && (
                    <p className="text-lg font-semibold text-[#0A2B1D]">
                      {formatCurrency(receipt.amount)}
                    </p>
                  )}
                  {receipt.date && (
                    <p className="text-xs text-[#5C6661]">{receipt.date}</p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditReceipt(receipt)}
                    className="flex-1 border-[#E6E8E6]"
                  >
                    <Edit3 className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  {!receipt.linked_expense_id && receipt.amount && receipt.date && (
                    <Button 
                      size="sm"
                      onClick={() => handleConvertToExpense(receipt.id)}
                      className="flex-1 bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
                    >
                      <ArrowRight className="w-4 h-4 mr-1" /> To Expense
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteReceipt(receipt.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Receipt Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#111614]">Edit Receipt</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#111614]">Vendor</Label>
              <Input
                value={editForm.vendor}
                onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })}
                placeholder="Store or vendor name"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div>
              <Label className="text-[#111614]">Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                placeholder="0.00"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div>
              <Label className="text-[#111614]">Date</Label>
              <Input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div>
              <Label className="text-[#111614]">Category</Label>
              <Select
                value={editForm.category_id}
                onValueChange={(val) => setEditForm({ ...editForm, category_id: val })}
              >
                <SelectTrigger className="bg-[#FAFAF8] border-[#E6E8E6]">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {expenseCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-[#111614]">Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="What was purchased"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(null)} className="border-[#E6E8E6]">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveReceipt}
              className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!showPreview} onOpenChange={() => setShowPreview(null)}>
        <DialogContent className="sm:max-w-2xl bg-white p-0">
          {showPreview && (
            <img 
              src={`${API}${showPreview.image_url}`} 
              alt="Receipt" 
              className="w-full h-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptsTab;
